import { useMemo, useEffect, useCallback } from 'react'
import map from 'lodash/map'
import isArray from 'lodash/isArray'
import commonApi from 'src/api/commonApi'
import { useSelector, useDispatch } from 'react-redux'

import { setCategories } from 'src/redux/actions/categoriesAction'
import { CATEGORY_TYPE } from 'src/libs/constant'
import calendarConfigApi from 'src/api/calendarApi'
import useGetWithContext from './useGetWithContext'
import { createRangeTimeOptions } from 'src/libs/date'
import centerApi from '../api/centerApi'

export function useGetCategories (type) {
  const dispatch = useDispatch()

  const categoriesList = useSelector((state) => state.categories[type])

  const api = useMemo(() => {
    switch (type) {
      case CATEGORY_TYPE.GRADE:
        return commonApi.getGrade
      case CATEGORY_TYPE.LEVEL:
        return commonApi.getLevel
      case CATEGORY_TYPE.DEVICE:
        return commonApi.getDevice
      case CATEGORY_TYPE.PROVINCE:
        return commonApi.getProvince
      case CATEGORY_TYPE.REGION:
        return commonApi.getRegion
      case CATEGORY_TYPE.SUBJECT:
        return commonApi.getSubject
      case CATEGORY_TYPE.STUDY_PROGRAM:
        return commonApi.getStudyProgram
      case CATEGORY_TYPE.CRM_PACKAGE:
        return commonApi.getCrmPackage
      case CATEGORY_TYPE.CLASS_TIME_SLOT:
        return calendarConfigApi.findAllTimeSlot
      case CATEGORY_TYPE.COUNTRY_PHONE:
        return commonApi.getCountryPhoneNumber
      case CATEGORY_TYPE.CENTER:
        return centerApi.getListCenterNotRole
      case CATEGORY_TYPE.VIOLATION_LEVEL:
        return commonApi.getViolationLevel
    }
  }, [type])

  const categories = useMemo(() => {
    return categoriesList
  }, [categoriesList, type])

  const { data: categoriesNew } = useGetWithContext(
    `FIND_ALL_${type}`,
    api,
    null,
    {
      isDisabled: !!categories || !type || !api
    }
  )

  const getCategoryData = useCallback((value, key = 'id') => {
    if (categoriesList && isArray(categoriesList) && categoriesList.length) {
      return categoriesList.find(category => category[key] === value)
    }
    return null
  }, [categoriesList])

  const categoryOptions = useMemo(() => {
    let rs = []
    if (!categoriesList || !categoriesList.length) return []
    switch (type) {
      case CATEGORY_TYPE.CLASS_TIME_SLOT:
        rs = createRangeTimeOptions(categoriesList, { optionLabelFormat: 'START - END', optionValueByKey: 'id' })
        break
      case CATEGORY_TYPE.LEVEL:
      case CATEGORY_TYPE.REGION:
      case CATEGORY_TYPE.GRADE:
        rs = map(categoriesList, (x) => ({ value: x.id, label: x.name, level: x.value, code: x.code }))
        break
      case CATEGORY_TYPE.COUNTRY_PHONE:
        rs = map(categoriesList, (x) => ({ value: x.code, label: `${x.dialCode} - ${x.name}` }))
        break
      case CATEGORY_TYPE.CENTER:
        rs = map(categoriesList, (x) => ({ value: x.id, label: `${x.code} - ${x.name}`, status: x.status }))
        break      
      case CATEGORY_TYPE.VIOLATION_LEVEL:
        rs = map(categoriesList, (x) => ({ value: x.id, label: x.name }))
        break
      default:
        rs = map(categoriesList, (x) => ({ value: x.id, label: x.name, code: x.code }))
    }
    return rs
  }, [type, categoriesList])

  const getOptions = useCallback(() => {
    let rs = []
    if (!categoriesList || !categoriesList.length) return []
    switch (type) {
      case CATEGORY_TYPE.CLASS_TIME_SLOT:
        rs = createRangeTimeOptions(categoriesList, { optionLabelFormat: 'START - END', optionValueByKey: 'id' })
        break
      case CATEGORY_TYPE.LEVEL:
      case CATEGORY_TYPE.REGION:
      case CATEGORY_TYPE.GRADE:
        rs = map(categoriesList, (x) => ({ value: x.id, label: x.name, level: x.value, code: x.code }))
        break
      case CATEGORY_TYPE.COUNTRY_PHONE:
        rs = map(categoriesList, (x) => ({ value: x.code, label: `${x.dialCode} - ${x.name}` }))
        break
      case CATEGORY_TYPE.CENTER:
        rs = map(categoriesList, (x) => ({ value: x.id, label: `${x.code} - ${x.name}` }))
        break
      case CATEGORY_TYPE.VIOLATION_LEVEL:
        rs = map(categoriesList, (x) => ({ value: x.id, label: x.name }))
        break
      default:
        rs = map(categoriesList, (x) => ({ value: x.id, label: x.name, code: x.code }))
    }
    return rs
  }, [type, categoriesList])

  useEffect(() => {
    if (categoriesNew) {
      const tempCategories = {}
      tempCategories[type] = categoriesNew
      dispatch(setCategories(tempCategories))
    }
  }, [categoriesNew])

  return { categories, getCategoryData, getOptions, categoryOptions }
}
